import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// UX-MEM-01 §0: the Member Light App is its OWN persona/shell (mobile-first, phone+OTP, dark
// "Tactical Minimalism" theme) — deliberately separate from DeskDataContext/AppDataContext, same
// reasoning as why Desk itself is separate from the Owner Cockpit. `home` bundles branding +
// occupancy + nudge + membership summary + module flags in ONE call (NFR-070: no N+1 on Home)
// and is shared across every tab via this context, refetched on demand via `reloadHome`.
const MemberDataContext = createContext(null);

export function MemberDataProvider({ children }) {
  const [me, setMe] = useState(null);
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const reloadHome = useCallback(async () => {
    const homeRes = await api.get("/member/home");
    setHome(homeRes.data);
    return homeRes.data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await api.get("/me");
      if (meRes.data.user_type !== "member") {
        navigate("/member/login");
        return;
      }
      setMe(meRes.data);
      await reloadHome();
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/member/login");
        return;
      }
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [navigate, reloadHome]);

  useEffect(() => { load(); }, [load]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout");
    navigate("/member/login");
  }, [navigate]);

  return (
    <MemberDataContext.Provider value={{ me, home, loading, error, reloadHome, reload: load, logout }}>
      {children}
    </MemberDataContext.Provider>
  );
}

export function useMemberData() {
  return useContext(MemberDataContext);
}
